import React, { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";
import {
  Add,
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import { 
  Character,
  Creature,
  TrueCreatureInstance,
  CustomParticipant,
  Encounter,
  // EncounterLog,
  EncounterParticipant,
  CharacterCondition,
} from "@dndModels";
import { DisablableOption } from "@models";
import { CR_OPTIONS } from "@dndModels/Constants";

import ShieldWhite from "../../../../../assets/img/Shield_white.png";
import ShieldBlack from "../../../../../assets/img/Shield_black.png";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import ColorButtonBox from "@components/input/buttons/ColorButtonBox";
import EditableLabel from "@components/input/strings/EditableLabel";
import HPInput from "@dndComponents/other/HPInput";
import CharacterResourceBoxesNew from "@dndComponents/character/misc/CharacterResourceBoxes";
import LoadingBox from "@components/input/LoadingBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ExpandButton from "@components/input/buttons/ExpandButton";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CreatureInstanceSavingThrows from "@dndComponents/creature/CreatureInstanceSavingThrows";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ClearButton from "@components/input/buttons/icon_buttons/ClearButton";
import StringBox from "@components/input/strings/StringBox";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import FlexBox from '@components/layout/FlexBox';
import ConditionBar from "@dndComponents/other/ConditionBar";
import DataUtilities from '@dndUtilities/data_utilities';

const darks = ["red","green","blue","purple","black","gray"];

interface Props { 
  e_participant: EncounterParticipant;
  encounter: Encounter;
  selected_participant_id: string;
  selected_ci_id: string;
  disabled?: boolean;
  change_time: number;
  onSelect: (ci: TrueCreatureInstance | CustomParticipant | null) => void;
  onChange: () => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function InitiativeTrackerParticipant({
  e_participant,
  encounter,
  selected_participant_id,
  selected_ci_id,
  disabled = false,
  change_time,
  onSelect,
  onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [get_creature, setGetCreature] = useState(false);
  const [creature, setCreature] = useState<Creature | undefined>(undefined);
  const [reloading, setReloading] = useState(false);
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [expanded, setExpanded] = useState(false);
  const [manager_open, setManagerOpen] = useState(false);
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    setReloading(true);
  }

  const resourcesChanged = () => {
    onChange();
    setReloading(true);
  }

  const dataTypeLoaded = useCallback((props: any) => {
    // console.log(props);
    if (props && props.value === "creature" && !creature) {
      setGetCreature(true);
    }
  }, []);

  const loadCreature = useCallback( async () => {
    if (e_participant.participant instanceof TrueCreatureInstance) {
      const creature_id = e_participant.participant.creature_id;
      const creatures = await api.getObjects<Creature>({ data_type: "creature" });
      const new_creature = creatures.find(o => o._id === creature_id);
      if (new_creature) {
        e_participant.participant.spirit.creature = new_creature;
        setCreature(new_creature);
      }
    }
  }, [e_participant]);

  useEffect(() => {
    if (get_creature) {
      setGetCreature(false);
      if (e_participant.participant instanceof TrueCreatureInstance && e_participant.participant.creature) {
        setCreature(e_participant.participant.creature);
      } else {
        setTimeout(() => {
          loadCreature();
        }, 100);
      }
    }
  }, [get_creature, e_participant, loadCreature]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      if (e_participant.participant instanceof TrueCreatureInstance && (e_participant.participant.creature || api.temp_hash["creature"])) {
        setGetCreature(true);
      }
      e_participant.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      e_participant.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
      api.subscription_holder.subscribe("Data Type Loaded", mounted, dataTypeLoaded);
    }
  }, [fetched, e_participant]);

  // eslint-disable-next-line
  useEffect(() => {
    if (change_time !== prev_change_time) {
      setChangeTime(change_time);
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const renderCRSelect = () => {
    if (!disabled) {
      if (creature_instance instanceof TrueCreatureInstance && 
        creature_instance.creature && 
        (creature_instance.creature.min_challenge_rating !== creature_instance.creature.challenge_rating || 
          creature_instance.creature.max_challenge_rating !== creature_instance.creature.challenge_rating)) {
        const options: DisablableOption[] = [];
        if (creature_instance.creature.name_table.length > 0) {
          for (let i = creature_instance.creature.min_challenge_rating; i <= creature_instance.creature.max_challenge_rating; ++i) {
            const name_for_cr = creature_instance.creature.name_table.find(o => o.level === i);
            if (name_for_cr) {
              options.push({ id: name_for_cr.name, text: name_for_cr.name, disabled: true });
            }
            options.push({ id: `${i}`, text: `${i}` });
          }
        } else {
          for (let i = creature_instance.creature.min_challenge_rating; i <= creature_instance.creature.max_challenge_rating; ++i) {
            options.push({ id: `${i}`, text: `${i}` });
          }
        }
        return (
          <FlexBox>
            <SelectStringBox
              name="Challenge Rating"
              options={options}
              value={`${creature_instance.challenge_rating}`}
              onChange={async (changed: string) => {
                const new_obj = creature_instance;
                // await new_obj.change_challenge_rating(+changed);
                onChange();
                setReloading(true);
              }}
            />
          </FlexBox>
        );
      } else if (e_participant.participant instanceof CustomParticipant) {
        return (
          <FlexBox>
            <SelectStringBox
              name="Challenge Rating"
              options={CR_OPTIONS}
              value={`${e_participant.participant.challenge_rating}`}
              onChange={async (changed: string) => {
                const new_obj = e_participant;
                if (new_obj.participant instanceof CustomParticipant) {
                  new_obj.participant.challenge_rating = +changed;
                  for (const cp of new_obj.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.challenge_rating = +changed;
                    }
                  }
                }
                onChange();
                setReloading(true);
              }}
            />
          </FlexBox>
        );
      }
    }
    return null;
  }

  const renderManage = () => {
    const participant = e_participant.participant;
    if (participant instanceof TrueCreatureInstance) {
      const others = encounter.participants.filter(o => 
        o.creature_id === e_participant.creature_id && 
        o.participant_id !== e_participant.participant_id);
      return (
        <ModalOrDrawer
          open={manager_open}
          onClose={() => {
            setManagerOpen(false);
            e_participant.subscription_holder.modalClosed();
          }}
        >
          <>
          { manager_open &&
            <FlexBox direction="column">
              <FlexBox className="header centered">Manage</FlexBox>
              <FlexBox>
                <StringBox
                  name="Group Name"
                  value={e_participant._name}
                  onBlur={(changed: string) => {
                    const new_obj = e_participant;
                    new_obj._name = changed;
                    onChange();
                    setPing(!ping);
                  }}
                />
              </FlexBox>
              { renderCRSelect() }
              <FlexBox>
                <Tooltip title={`Add Another`}>
                  <span>
                    <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                      disabled={ disabled }
                      onClick={ async () => {
                        if (e_participant.participant instanceof TrueCreatureInstance) {
                          const new_obj = e_participant;
                          const new_creature_inst = new TrueCreatureInstance();
                          new_creature_inst.copy(e_participant.participant, true);
                          const in_encounter = e_participant.child_participants.length;
                          if (creature) {
                            new_creature_inst.name = creature.name;
                          }
                          new_creature_inst.name += ` (${in_encounter + 1})`;
                          new_obj.child_participants.push(new_creature_inst);
                          onChange();
                          setReloading(true);
                        }
                      }}>
                      <Add/>
                    </Fab>
                  </span>
                </Tooltip>
                &nbsp;{ creature ? creature.name : "" }
              </FlexBox>
              { !reloading && e_participant.child_participants.map((child, key) => {
                if (child instanceof TrueCreatureInstance) {
                  return (
                    <FlexBox key={child.true_id} direction="row" className={ key % 2 === 0 ? "alternate" : "" }>
                      <FlexBox className="centered">{ child.use_name } (HP: {child.hp.current})</FlexBox>
                      <FlexBox xs_expand={0} style={{ width: "33%" }}>
                        <ButtonBox
                          name="Split"
                          disabled={ disabled || e_participant.child_participants.length === 1 }
                          onClick={() => {
                            // debugger;
                            const new_obj = e_participant;
                            const new_ci = new EncounterParticipant();
                            new_ci.creature_id = new_obj.creature_id;
                            new_ci.participant_id = child.true_id;
                            new_ci.owner_id = new_obj.owner_id;
                            new_ci.color = new_obj.color;
                            new_ci.participant_type = new_obj.participant_type;
                            new_ci.participant = child;
                            new_ci.child_participants = [child];
                            encounter.participants.push(new_ci);
                            new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof TrueCreatureInstance && o.true_id !== child.true_id);
                            
                            if (new_obj.participant instanceof TrueCreatureInstance && new_obj.participant.true_id === child.true_id) {
                              new_obj.participant = new_obj.child_participants[0];
                            }
                            onChange();
                            setReloading(true);
                          }}
                        />
                      </FlexBox>
                      <FlexBox xs_expand={0} style={{ width: "10%" }}>
                        <ClearButton className="float-right"
                          disabled={ disabled }
                          onClick={ () => {
                            if (e_participant.child_participants.length === 1) {
                              encounter.participants = encounter.participants.filter(o => o.participant_id !== e_participant.participant_id);
                              
                              onChange();
                              setManagerOpen(false);
                            } else {
                              const new_obj = e_participant;
                              new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof TrueCreatureInstance && o.true_id !== child.true_id);
                              
                              if (new_obj.participant instanceof TrueCreatureInstance && new_obj.participant.true_id === child.true_id) {
                                new_obj.participant = new_obj.child_participants[0];
                              }
                              onChange();
                              setReloading(true);
                            }
                          }} 
                        />
                      </FlexBox>
                    </FlexBox>
                  );
                }
                return null;
              })}
              { others.length > 0 &&
                <>
                  <FlexBox className="header centered">Join</FlexBox>
                  <FlexBox direction="row">
                    { !reloading && others.map((other, key) => {
                      return (
                        <FlexBox key={key} className="clickable"
                          onClick={() => {
                            const new_obj = e_participant;
                            new_obj.child_participants = [...new_obj.child_participants,...other.child_participants];
                            other.child_participants = [];
                            encounter.participants = encounter.participants.filter(o => o.participant_id !== other.participant_id);
                            
                            onChange();
                            setReloading(true);
                          }}>
                          { other.name }
                        </FlexBox>
                      );
                    })}
                  </FlexBox>
                </>
              }
            </FlexBox>
          }
          </>
        </ModalOrDrawer>
      );
    } else if (participant instanceof CustomParticipant) {
      // debugger;
      return (
        <ModalOrDrawer
          open={manager_open}
          onClose={() => {
            setManagerOpen(false);
            e_participant.subscription_holder.modalClosed();
          }}
        >
          <FlexBox direction="row">
            <FlexBox className="header centered">Manage</FlexBox>
            <FlexBox>
              <StringBox
                name="Name"
                value={participant.name}
                disabled={disabled}
                onBlur={(changed: string) => {
                  participant.name = changed;
                  for (const cp of e_participant.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.name = changed;
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox>
              <StringBox
                name="Max Hit Points"
                value={`${participant.hp.max}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_hp = +changed;
                  const old_hp = participant.hp.max;
                  participant.hp.max = new_hp;
                  if (participant.hp.current === old_hp) {
                    participant.hp.current = new_hp;
                  }
                  participant.subscription_holder.resourcesChanged();
                  for (const cp of e_participant.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.hp.max = new_hp;
                      if (cp.hp.current === old_hp) {
                        cp.hp.current = new_hp;
                      }
                      cp.subscription_holder.resourcesChanged();
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox>
              <StringBox
                name="Armor Class"
                value={`${participant.armor_class}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: string) => {
                  participant.armor_class = +changed;
                  for (const cp of e_participant.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.armor_class = +changed;
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox>
              <SelectStringBox
                name="Challenge Rating"
                value={`${participant.challenge_rating}`}
                options={CR_OPTIONS}
                disabled={disabled}
                onChange={(changed: string) => {
                  participant.challenge_rating = +changed;
                  for (const cp of e_participant.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.challenge_rating = +changed;
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox>
              <StringBox
                name="Initiative Modifier"
                value={`${participant.initiative_modifier}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: string) => {
                  participant.initiative_modifier = +changed;
                  onChange();
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox>
              <Tooltip title={`Add Another`}>
                <span>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    disabled={ disabled }
                    onClick={ async () => {
                      // debugger;
                      const new_obj = e_participant;
                      const new_cp = participant.clone();
                      new_cp.true_id = uuidv4().toString();
                      new_obj.child_participants.push(new_cp);
                      onChange();
                      setPing(!ping);
                    }}>
                    <Add/>
                  </Fab>
                </span>
              </Tooltip>
            </FlexBox>
            <FlexBox direction="row">
              { !reloading && e_participant.child_participants.map((child, key) => {
                if (child instanceof CustomParticipant) {
                  return (
                    <FlexBox key={child.true_id} direction="row" className={ key % 2 === 0 ? "alternate" : "" }>
                      <FlexBox>{ child.name } (HP: {child.hp.current})</FlexBox>
                      <FlexBox>
                        <ButtonBox
                          name="Split"
                          disabled={ disabled || e_participant.child_participants.length === 1 }
                          onClick={() => {
                            const new_obj = e_participant;
                            const new_ci = new EncounterParticipant();
                            new_ci.participant_id = child.true_id;
                            new_ci.creature_id = new_obj.creature_id;
                            new_ci.participant_id = child.true_id;
                            new_ci.owner_id = new_obj.owner_id;
                            new_ci.color = new_obj.color;
                            new_ci.participant_type = new_obj.participant_type;
                            new_ci.participant = child;
                            new_ci.child_participants = [child];
                            encounter.participants.push(new_ci);
                            new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof TrueCreatureInstance && o.true_id !== child.true_id);
                            
                            if (new_obj.participant instanceof TrueCreatureInstance && new_obj.participant.true_id === child.true_id) {
                              new_obj.participant = new_obj.child_participants[0];
                            }
                            onChange();
                            setPing(!ping);
                          }}
                        />
                      </FlexBox>
                      <FlexBox>
                        <ClearButton
                          disabled={ disabled }
                          onClick={ () => {
                            if (e_participant.child_participants.length === 1) {
                              encounter.participants = encounter.participants.filter(o => o.participant_id !== e_participant.participant_id);
                              
                              onChange();
                              setManagerOpen(false);
                            } else {
                              const new_obj = e_participant;
                              new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof TrueCreatureInstance && o.true_id !== child.true_id);
                              
                              if (new_obj.participant instanceof TrueCreatureInstance && new_obj.participant.true_id === child.true_id) {
                                new_obj.participant = new_obj.child_participants[0];
                              }
                              onChange();
                              setPing(!ping);
                            }
                          }} 
                        />
                      </FlexBox>
                    </FlexBox>
                  );
                }
                return null;
              })}
            </FlexBox>
          </FlexBox>
        </ModalOrDrawer>
      );
    }
    return null;
  }

  const renderInfo = () => {
    let return_me: any = null;
    if (!(e_participant.participant instanceof Character)) {
      return_me = (
        <>
          <DandDButtonBox
            name="Manage"
            disabled={disabled}
            color={ e_participant.color && !darks.includes(e_participant.color) ? "black" : (e_participant.color ? "white" : undefined)}
            onClick={() => {
              // debugger;
              setManagerOpen(true);
            }}
          />
          { manager_open && renderManage() }
        </>
      );
    }
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          <FlexBox>
            { !disabled &&
              <ToggleButtonBox 
                name="Include in Difficulty"
                value={ e_participant.include_in_difficulty }
                disabled={ disabled }
                color={ e_participant.color && !darks.includes(e_participant.color) ? "black" : undefined}
                onToggle={ () => {
                  const new_obj = e_participant;
                  if (new_obj) {
                    new_obj.include_in_difficulty = !new_obj.include_in_difficulty;
                  }
                  onChange();
                }} 
              />
            }
          </FlexBox>
          <FlexBox>{ return_me }</FlexBox>
          <FlexBox xs_expand={0}>
            { !disabled &&
              <ColorButtonBox 
                color={e_participant.color}
                onChange={(changed: string) => {
                  const new_obj = e_participant
                  new_obj.color = changed;
                  setPing(!ping);
                }}
              />
            }
          </FlexBox>
        </FlexBox>
        { e_participant.participant instanceof TrueCreatureInstance &&
          <>
            <FlexBox className="header centered">
              Saving Throws
            </FlexBox>
            <FlexBox>
              <CreatureInstanceSavingThrows 
                obj={e_participant.participant}
                disabled={ disabled }
                trim
                color={e_participant.color}
                onChange={() => {

                }}
              />
            </FlexBox>
          </>
        }
      </FlexBox>
    );
  }

  const renderSlots = (creature_instance: TrueCreatureInstance | null) => {
    if (creature_instance) {
      return creature_instance.use_slots2.map((r, key) => {
        const level = +r.resource_id.substring(6);
        const slot_count = r.total;
        if (slot_count && slot_count > 0) {
          let level_name = "";
          switch(+level) {
            case 1: level_name = "1st"; break;
            case 2: level_name = "2nd"; break;
            case 3: level_name = "3rd"; break;
            default: level_name = `${level}th`; break;
          }
          return (
            <FlexBox key={key}>
              {/* <CharacterResourceBoxesNew 
                used={creature_instance.used_slots[+level]}
                total={slot_count}
                true_total={slot_count}
                created={0}
                type={`${level_name} Level Slot${ slot_count > 0 && "s" }`}
                shared={false}
                onChange={(used: number) => {
                  if (!disabled) {
                    const new_obj = e_participant;
                    const ci = new_obj.participant as TrueCreatureInstance;

                    ci.used_slots[+level] = used;
                    ci.subscription_holder.resourcesChanged();
                    setPing(!ping);
                  }
                }}
              /> */}
            </FlexBox>
          );
        } else {
          return null;
        }
      });
    }
    return null;
  }

  const participantName = () => {
    if (e_participant.participant_type === "Lair Actions") {
      return "Lair Actions";
    } else {
      return e_participant.name;
    }
  }

  const api_user_id = api.user ? api.user._id : "";
  const my_turn = encounter.current_turn === e_participant.turn_order;
  const selected = selected_participant_id === e_participant.participant_id;
  const creature_instance: TrueCreatureInstance | null = e_participant.participant && e_participant.participant instanceof TrueCreatureInstance ? e_participant.participant : null;
  const bg_class_name = `bg_${e_participant.color}${e_participant.include_in_difficulty ? "" : "_striped"}`;

  const renderConditions = (creature_instance: TrueCreatureInstance) => {
    return (
      <FlexBox>
        <ConditionBar
          char_int={creature_instance}
          active_conditions={creature_instance.condition_hash}
          starred={[]}
          immune={creature_instance.condition_immunities}
          // extra_ids={["Any"]}
          disabled={disabled}
          onChange={(condition_id: string, level: number) => {
            const new_obj = creature_instance;
            if (level === 0) {
              // delete new_obj.condition_hash[condition_id];
              new_obj.conditions = new_obj.conditions.filter(o => o.source_id !== condition_id);
            } else {
              let char_cond = new_obj.conditions.find(o => o.source_id === condition_id);
              if (!char_cond) {
                const cond = data_util.conditions.find(o => o._id === condition_id);
                if (cond) {
                  char_cond = new CharacterCondition();
                  char_cond.source_id = condition_id;
                  // char_cond.copy_source({ char: new_obj, feature_source: cond });
                  new_obj.conditions.push(char_cond);
                }
              }
              if (char_cond) {
                char_cond.level = level;
              }
              // new_obj.condition_hash[condition_id] = { level, source_true_id: "" };
            }
            onChange();
            setPing(!ping);
          }}
          onStar={(id: string) => {
          }}
          onImmune={(id: string) => {
            const new_obj = creature_instance;
            if (new_obj.condition_immunities.includes(id)) {
              new_obj.spirit.condition_immunities = new_obj.condition_immunities.filter(o => o !== id);
            } else {
              new_obj.condition_immunities.push(id);
            }
            onChange();
            setPing(!ping);
          }} 
        />
      </FlexBox>
    );
  }

  const renderExpanded = () => {
    if (expanded) {
      if (name === "Loading") {
        return (
          <FlexBox><LoadingBox /></FlexBox>
        );
      } else if (e_participant.participant && e_participant.child_participants.length < 2) {
        return (
          <>
            { renderInfo() }
            <FlexBox direction="row">
              { renderSlots(creature_instance) }
            </FlexBox>
            { creature_instance && renderConditions(creature_instance) }
            { creature_instance && creature && creature_instance.legendary_actions_per_round > 0 && (e_participant.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
              <FlexBox>
                <CharacterResourceBoxesNew 
                  used={creature_instance.used_legendary_actions}
                  total={creature_instance.legendary_actions_per_round}
                  true_total={creature_instance.legendary_actions_per_round}
                  created={0}
                  type={"Legendary Actions"}
                  // show_type={false}
                  shared={false}
                  onChange={(used: number) => {
                    if (!disabled) {
                      const new_obj = e_participant;
                      const part = new_obj.participant;
                                    
                      if (part instanceof TrueCreatureInstance) {
                        part.body.resource_holder.used_legendary_actions = used;
                        new_obj.subscription_holder.resourcesChanged();
                        setPing(!ping);
                      }
                    }
                  }}
                />
              </FlexBox>
            }
            { creature_instance && creature && creature_instance.legendary_resistances > 0 && (e_participant.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
              <FlexBox>
                <CharacterResourceBoxesNew 
                  used={creature_instance.used_legendary_resistances}
                  total={creature_instance.legendary_resistances}
                  true_total={creature_instance.legendary_resistances}
                  created={0}
                  type={"Legendary Resistances"}
                  // show_type={false}
                  shared={false}
                  onChange={(used: number) => {
                    if (!disabled) {
                      const new_obj = e_participant;
                      const part = new_obj.participant;
                                    
                      if (part instanceof TrueCreatureInstance) {
                        part.body.resource_holder.used_legendary_resistances = used;
                        new_obj.subscription_holder.resourcesChanged();
                        setPing(!ping);
                      }
                    }
                  }}
                />
              </FlexBox>
            }
          </>
        );
      } else if (e_participant.participant instanceof TrueCreatureInstance || 
        e_participant.participant instanceof CustomParticipant) {
        return (
          <>
            { renderInfo() }
            <FlexBox>
              { api.user && encounter.owner_id === api.user._id &&
                <ToggleButtonBox 
                  name="Include in Difficulty"
                  value={ e_participant.include_in_difficulty }
                  disabled={ disabled }
                  color={ e_participant.color && !darks.includes(e_participant.color) ? "black" : undefined}
                  onToggle={ () => {
                    const new_obj = e_participant;
                    if (new_obj) {
                      new_obj.include_in_difficulty = !new_obj.include_in_difficulty;
                    }
                    onChange();
                  }} 
                />
              }
            </FlexBox>
            { e_participant.child_participants.map(ci => {
              if (ci instanceof TrueCreatureInstance || ci instanceof CustomParticipant) {
                const ci_selected = selected_ci_id === ci.true_id;
                return (
                  <FlexBox key={ci.true_id} direction="column"
                    style={{ 
                      border: (my_turn ? "4px solid #138f13" : (ci_selected ? "2px solid blue" : "2px solid black")),
                      color: darks.includes(e_participant.color) ? "white" : "black",
                      padding: "4px"
                    }}>
                    <FlexBox direction="row" className={bg_class_name}>
                      <FlexBox className={`${disabled ? "" : "clickable"}`}
                        onClick={() => {
                          if (!disabled) {
                            onSelect(ci);
                          }
                        }}>
                        { ci.use_name }
                      </FlexBox>
                      <FlexBox xs_expand={0} className="float-right">
                        <HPInput
                          char={ci}
                          disabled={ disabled }
                          borderColor={ darks.includes(e_participant.color) ? "white" : "black" }
                        />
                      </FlexBox>
                    </FlexBox>
                    { ci instanceof TrueCreatureInstance &&
                      <>
                        <FlexBox direction="row">
                          { renderSlots(ci) }
                        </FlexBox>
                        { renderConditions(ci) }
                        { ci.legendary_actions_per_round > 0 && (e_participant.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
                          <FlexBox>
                            <CharacterResourceBoxesNew 
                              used={ci.used_legendary_actions}
                              total={ci.legendary_actions_per_round}
                              true_total={ci.legendary_actions_per_round}
                              created={0}
                              type={"Legendary Actions"}
                              shared={false}
                              onChange={(used: number) => {
                                if (!disabled) {
                                  ci.body.resource_holder.used_legendary_actions = used;
                                  ci.subscription_holder.resourcesChanged();
                                  setPing(!ping);
                                }
                              }}
                            />
                          </FlexBox>
                        }
                        { ci.legendary_resistances > 0 && (e_participant.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
                          <FlexBox>
                            <CharacterResourceBoxesNew 
                              used={ci.used_legendary_resistances}
                              total={ci.legendary_resistances}
                              true_total={ci.legendary_resistances}
                              created={0}
                              type={"Legendary Resistances"}
                              shared={false}
                              onChange={(used: number) => {
                                if (!disabled) {
                                  ci.body.resource_holder.used_legendary_resistances = used;
                                  ci.subscription_holder.resourcesChanged();
                                  setPing(!ping);
                                }
                              }}
                            />
                          </FlexBox>
                        }
                      </>
                    }
                  </FlexBox>
                );
              }
              return null;
            })}
          </>
        );
      } else if (e_participant.participant instanceof Character) {
        return renderInfo();
      }
    }
    return null;
  }

  const renderMainRow = () => {
    return (
      <FlexBox direction="row">
        { encounter.running &&
          <FlexBox direction="column">
            <FlexBox>
              { encounter.running ? e_participant.turn_order : "" }
            </FlexBox>
            { my_turn &&
              <FlexBox>&rarr;</FlexBox>
            }
          </FlexBox>
        }
        <FlexBox xs_expand={0} className="centered" style={{ width: "20%" }}>
          <EditableLabel 
            name="Init"
            value={ e_participant.initiative === undefined ? "" : `${e_participant.initiative}` }
            display_name
            fontSize={16}
            color={darks.includes(e_participant.color) ? "white" : "black"}
            disabled={disabled || encounter.running}
            onChange={(changed: string) => {
              const new_obj = e_participant;
              if (new_obj) {
                if (changed === "") {
                  new_obj.initiative = undefined;
                } else {
                  new_obj.initiative = common_util.clean_number_string(changed);
                }
              }
              onChange();
            }}
          />
        </FlexBox>
        <FlexBox className={disabled ? "centered" : `centered clickable`}
          onClick={() => {
            if (!disabled) {
              onSelect(null);
            }
          }}>
          <FlexBox direction="column">
            <FlexBox>{ name }</FlexBox>
            { e_participant.participant instanceof Character ?
              <FlexBox className="small_label gray">
                { e_participant.participant.race_and_class_string.replace("(","").replace(")","") }
              </FlexBox>
            : e_participant.participant instanceof TrueCreatureInstance ?
              <FlexBox className="small_label gray">
                {e_participant.participant.creature_type}, CR: {e_participant.participant.challenge_rating}
              </FlexBox>
            : e_participant.participant instanceof CustomParticipant &&
              <FlexBox className="small_label gray">
                CR: { e_participant.participant.challenge_rating }
              </FlexBox>
            }
          </FlexBox>
        </FlexBox>
        <FlexBox xs_expand={0} className="centered" style={{ position: "relative" }}>
          { !disabled &&
            <>
              <img src={ darks.includes(e_participant.color) ? ShieldWhite : ShieldBlack } alt="shield" 
                style={{
                  position: "absolute",
                  width: "30px",
                  height: "30px",
                  zIndex: 2
                }}
              />
              <div className="centered" style={{
                width: "30px",
                height: "30px"
              }}>
                <span style={{ lineHeight: "1.7" }}>
                  { e_participant.ac }
                </span>
              </div>
            </>
          }
        </FlexBox>
        { e_participant.participant && !(e_participant.participant instanceof Character) &&
          <FlexBox className="centered">
            <HPInput
              char={e_participant.participant}
              disabled={ disabled }
              borderColor={ darks.includes(e_participant.color) ? "white" : "black" }
            />
          </FlexBox>
        }
      </FlexBox>
    );
  }

  const renderSecondRow = () => {
    return (
      <FlexBox direction="row">
        <FlexBox>
          { e_participant.name !== "Lair Actions" && 
            <ExpandButton 
              is_expanded={expanded}
              onClick={() =>{
                setExpanded(!expanded);
              }}
            />
          }
        </FlexBox>
        <FlexBox>
          { api.user && encounter.campaign && encounter.campaign.owner_id === api.user._id &&
            <ClearButton className="float-right"
              disabled={ disabled || e_participant.turn_order === encounter.current_turn }
              onClick={ () => {
                encounter.participants = encounter.participants.filter(o => o.participant_id !== e_participant.participant_id);
                
                onChange();
              }} 
            />
          }
        </FlexBox>
      </FlexBox>
    );
  }

  const name = participantName();
  return (
    <FlexBox direction="column" className={bg_class_name}
      style={{ 
        border: (my_turn ? "4px solid #138f13" : (selected ? "2px solid blue" : "2px solid black")),
        // backgroundColor: obj.color,
        color: darks.includes(e_participant.color) ? "white" : "black",
        padding: "4px"
      }}>
      { renderMainRow() }
      { renderSecondRow() }
      { renderExpanded() }
    </FlexBox>
  );
}
